/* oxlint-disable */
"use client";

import React, { useEffect, useRef, useState } from "react";

import dynamic from "next/dynamic";

import { useRouter, useSearchParams } from "next/navigation";
import Script from "next/script";

import { useAuthStore } from "@/stores/userStore";
import { userUIStore } from "@/stores/userUIStore";

import { getCurrencyByCountry } from "@/utils/currency";

import { useAuth } from "@/core/auth/client/AuthProvider";
import { encrypt } from "@/utils/core";

const LoginModal = dynamic(
  () => {
    return import("@/components/modals/LoginModal");
  },
  {
    ssr: false,
    loading: () => {
      return null;
    },
  }
);
// Helper function to get integration based on currency
const getIntegrationByCurrency = (currency?: string): string => {
  switch (currency) {
    case "GTQ":
      return process.env.NEXT_PUBLIC_ALTENAR_INTEGRATION_GT!;
    case "DOP":
      return process.env.NEXT_PUBLIC_ALTENAR_INTEGRATION_DO!;
    default:
      return process.env.NEXT_PUBLIC_ALTENAR_INTEGRATION!;
  }
};

const SportsBook = () => {
  const { session } = useAuth();

  const widgetRef = useRef(null);
  const hasAuthenticatedRef = useRef<boolean>(false);
  const scriptUrl = process.env.NEXT_PUBLIC_SCRIPT_URL as string;

  // State for detected country/currency for unauthenticated users
  const [detectedCurrency, setDetectedCurrency] = useState<string>("USD");
  const [isDetectingLocation, setIsDetectingLocation] = useState<boolean>(
    !session?.user
  );

  // Get integration based on user's currency (authenticated) or detected currency (unauthenticated)
  const userCurrency = detectedCurrency;
  console.log("userCurrency>>>>>>>", userCurrency);
  const integration = getIntegrationByCurrency(userCurrency);
  console.log("integration>>>>>>>", integration);
  const [isScriptLoaded, setIsScriptLoaded] = useState<boolean>(false);
  const [isWidgetInitialized, setIsWidgetInitialized] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { isSidebarOpen, toggleLoginModal } = userUIStore();
  const searchParams = useSearchParams();
  const leagueLink = searchParams.get("leagueLink");
  const eventLink = searchParams.get("event");
  const shareCode = searchParams.get("shareCode");
  // const reservationCode = searchParams.get('reservationCode');
  const { account } = useAuthStore();
  const router = useRouter();

  // Detect user's location for unauthenticated users
  useEffect(() => {
    const detectLocation = async () => {
      // Skip if user is authenticated (use their currency)
      // if (session?.user) {
      //   setIsDetectingLocation(false);
      //   return;
      // }

      try {
        setIsDetectingLocation(true);
        const response = await fetch("/api/detect-country");
        const data = await response.json();
        const countryCode = data.countryCode || "GLOBAL";
        const currency = getCurrencyByCountry(countryCode);

        console.log("Detected country for unauthenticated user:", countryCode);
        console.log("Derived currency:", currency);

        setDetectedCurrency(currency);
      } catch (error) {
        console.warn("Location detection failed, using default USD:", error);
        setDetectedCurrency("USD");
      } finally {
        setIsDetectingLocation(false);
      }
    };

    detectLocation();
  }, []);

  useEffect(() => {
    if (account?.contingencies?.bets) {
      router.push("/?cont=2");
    }
  }, [account, router]);

  let leagueType = null;
  let leagueId = null;

  if (leagueLink) {
    const parts = leagueLink.split("/");
    if (parts.length === 2) {
      leagueType = parts[0]; // 'championship' or 'category'
      leagueId = Number.parseInt(parts[1], 10); // Convert ID to number
    }
  }

  useEffect(() => {
    const handleSignInClick = () => {
      setShowLoginModal(true);
      toggleLoginModal();
    };

    (window as any).handleSignInClick = handleSignInClick;
  }, [toggleLoginModal]);

  // Initialize Altenar SDK once when script loads (should not depend on session)
  useEffect(() => {
    const initSportsBook = async () => {
      // Wait for location detection to complete before initializing
      if (isDetectingLocation) {
        console.log("Waiting for location detection...");
        return;
      }

      // Only initialize if not already initialized
      if ((widgetRef as any).current) {
        console.log("Widget already initialized, skipping...");
        return;
      }

      console.log(
        "Initializing Altenar SDK with integration:",
        integration,
        "for currency:",
        userCurrency,
        "userId:",
        session?.user?.id
      );

      // Initialize SDK with encrypted token if user is logged in
      // Altenar backend expects token in format: encryptedData#authTag
      const initConfig: any = {
        integration,
        culture: "es-ES",
        themeName: "dark",
      };

      if (session?.user?.id) {
        try {
          const encryptedUserId: any = await encrypt(session.user.id);
          if (encryptedUserId?.data && encryptedUserId?.tag) {
            initConfig.token = `${encryptedUserId.data}#${encryptedUserId.tag}`;
            console.log("[Altenar Init] Passing encrypted token:", initConfig.token.substring(0, 50) + "...");
          } else {
            console.error("[Altenar Init] Encryption failed:", encryptedUserId);
          }
        } catch (error) {
          console.error("[Altenar Init] Error encrypting user ID:", error);
        }
      }

      (window as any).altenarWSDK.init(initConfig);

      // Add the sportsbook widget
      (widgetRef as any).current = (window as any).altenarWSDK.addSportsBook({
        props: {
          shareCode: shareCode || undefined,
          onShareCodeChange: ({
            message,
            code,
            link,
          }: {
            message: string;
            code: string;
            link: string;
          }) => {},
          eventId: eventLink ? Number(eventLink) : undefined,
          page:
            leagueType === "championship" || leagueType === "category"
              ? leagueType
              : undefined,
          championshipIds:
            leagueType === "championship" ? [leagueId] : undefined,
          categoryIds: leagueType === "category" ? [leagueId] : undefined,
          onSignInButtonClick: (window as any).handleSignInClick,
          backgroundOverride: {
            sport: {
              66: "https://cdn.sanity.io/images/mavwf57a/production/413b526a262b3e12b000b3671d779c58362de3e1-400x141.webp",
              67: "https://cdn.sanity.io/images/mavwf57a/production/0cb2696d8a088415ee2cdb14e152e9247db47b77-1792x1024.webp",
              68: "https://cdn.sanity.io/images/mavwf57a/production/8c8e1b0d1e029261a620e0890aee4897eeb87ba2-1792x1024.webp",
              76: "https://cdn.sanity.io/images/mavwf57a/production/2b74598240c46346d5717df0446ad3a50a3fd75b-1792x1024.webp",
            },
            championship: {
              2936: "https://cdn.sanity.io/images/mavwf57a/production/5a5b7177a6a5351e49f8247395e92be6f6d91b7a-400x141.png",
              2941: "https://cdn.sanity.io/images/mavwf57a/production/9554bdda86eeadf7e69113191546c01fe53b3954-400x141.png",
              2942: "https://cdn.sanity.io/images/mavwf57a/production/0ad213d3f09f1d47364f9034ceab83e38a6266f1-400x141.png",
              16808:
                "https://cdn.sanity.io/images/mavwf57a/production/052d85e593399ca302da16156cf0df13eed2af99-400x141.png",
              3709: "https://cdn.sanity.io/images/mavwf57a/production/10497278e15253bc8f7b139d92acd5c27a8e1070-400x141.png",
              3685: "https://cdn.sanity.io/images/mavwf57a/production/b0ad17f6532a9445022289c9e822433cb7006b3a-400x141.png",
              17135:
                "https://cdn.sanity.io/images/mavwf57a/production/14305ec6ee0f818e5a7079a3620891dbc0dba422-400x141.png",
              32729:
                "https://cdn.sanity.io/images/mavwf57a/production/14305ec6ee0f818e5a7079a3620891dbc0dba422-400x141.png",
            },
          },
          scrollerControls: 3,
          showScorePreview: true,
          variants: {
            BannerEventBox: 4,
          },
        },
        tokens: {
          OddBox: {
            borderWidth: 1,
            borderColor: "#7b27d8",
            background: "#212121",
            oddColor: "white",
            labelColor: "white",
          },
          OddBoxSelected: {
            background: "#7b27d8",
            oddColor: "#7B27D8c",
            labelColor: "#7B27D8c",
          },
        },
        container: document.getElementById("sbook"),
      });

      console.log("Altenar SDK initialized successfully");
      setIsWidgetInitialized(true);
    };

    if (isScriptLoaded && !isDetectingLocation) {
      initSportsBook();
    }

    return () => {
      if ((widgetRef as any).current) {
        try {
          (widgetRef as any).current.remove();
          (widgetRef as any).current = null;
        } catch (error) {
          console.warn("Error cleaning up widget:", error);
        }
      }
      setIsWidgetInitialized(false);
    };
  }, [
    eventLink,
    integration,
    isScriptLoaded,
    leagueId,
    leagueType,
    shareCode,
    isDetectingLocation,
    userCurrency,
    session?.user?.id,
  ]);

  // Handle login/logout for users who authenticate after widget is initialized
  // Altenar backend expects token in format: encryptedData#authTag
  useEffect(() => {
    const updateAuthState = async () => {
      // Don't try to update if script not loaded, widget not initialized, or detecting location
      if (!isScriptLoaded || !isWidgetInitialized || isDetectingLocation) {
        return;
      }

      // Skip if altenarWSDK is not available
      if (!(window as any).altenarWSDK) {
        return;
      }

      try {
        if (session?.user?.id) {
          // User is logged in - encrypt the user ID for Altenar
          console.log("[Altenar Auth] Encrypting user ID:", session.user.id);
          const encryptedUserId: any = await encrypt(session.user.id);

          if (typeof encryptedUserId === 'string' || !encryptedUserId?.data || !encryptedUserId?.tag) {
            console.error("[Altenar Auth] Encryption failed:", encryptedUserId);
            return;
          }

          const token = `${encryptedUserId.data}#${encryptedUserId.tag}`;
          console.log("[Altenar Auth] Setting encrypted token:", token.substring(0, 50) + "...");

          (window as any).altenarWSDK.set({
            token: token,
          });
          console.log("[Altenar Auth] Token set successfully");
          hasAuthenticatedRef.current = true;
        } else if (hasAuthenticatedRef.current) {
          // User is logged out - only sign out if they were previously logged in
          console.log("[Altenar Auth] Signing out user from Altenar");
          (window as any).altenarWSDK.set({
            token: "",
          });
          hasAuthenticatedRef.current = false;
        }
        // If user was never logged in, don't call set() - SDK handles unauthenticated state
      } catch (error) {
        console.error("[Altenar Auth] Error updating auth state:", error);
      }
    };

    updateAuthState();
  }, [isScriptLoaded, isWidgetInitialized, session?.user?.id, isDetectingLocation]);

  return (
    <>
      <Script
        src={`${scriptUrl}/altenarWSDK.js`}
        strategy="afterInteractive"
        onReady={() => {
          setIsScriptLoaded(true);
        }}
      />
      <div
        className={`${isSidebarOpen ? "pl-72" : "pl-20"} w-full pt-20 max-lg:pt-[3rem] max-md:pl-1 bg-default-50/30`}
      >
        <div
          className="text-default-900 flex flex-col items-stretch w-full mt-3"
          id="mainTab"
          role="tabpanel"
          tabIndex={0}
        >
          {account?.contingencies?.bets ? (
            <div className="flex flex-col items-center justify-center h-screen">
              <div className="flex flex-col items-center justify-center bg-red-600 p-6 rounded-xl">
                <h1 className="text-2xl font-bold">
                  Contingencia de pronosticos deportivas
                </h1>
                <p className="text-lg">
                  Comunicate con el servicio de pronosticos para resolver el
                  problema.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-default-50/30">
              <div className="" id="sportsbookTab" role="tabpanel">
                <div className="w-full mx-auto p-0">
                  <div className="flex flex-wrap">
                    <div className="w-full pb-[140px]">
                      {isDetectingLocation && (
                        <div className="flex items-center justify-center h-64">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sivarbet-primary mx-auto mb-4" />
                            <p className="text-sivarbet-text">
                              Detectando ubicación...
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="altenar-sportsbook-wrapper relative">
                        <div id="sbook" className="min-h-[calc(100vh-200px)]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showLoginModal && <LoginModal />}
      <style jsx global>{`
        /* Limitar altura del cupón de Altenar */
        .altenar-betslip,
        .altenar-coupon,
        [class*="betslip"],
        [class*="Betslip"],
        [class*="coupon"],
        [class*="Coupon"] {
          max-height: calc(100vh - 160px) !important;
          overflow-y: auto !important;
        }
        
        /* Ajuste específico para el widget flotante del cupón */
        .altenar-widget-betslip,
        .altenar-fixed-coupon,
        div[id*="betslip"],
        div[id*="coupon"] {
          max-height: calc(100vh - 160px) !important;
          top: 100px !important;
          bottom: auto !important;
          overflow-y: auto !important;
        }
        
        /* Scrollbar styling para el cupón */
        .altenar-betslip::-webkit-scrollbar,
        .altenar-coupon::-webkit-scrollbar,
        [class*="betslip"]::-webkit-scrollbar {
          width: 6px;
        }
        
        .altenar-betslip::-webkit-scrollbar-thumb,
        .altenar-coupon::-webkit-scrollbar-thumb,
        [class*="betslip"]::-webkit-scrollbar-thumb {
          background-color: #7b27d8;
          border-radius: 3px;
        }
      `}</style>
    </>
  );
};

export default SportsBook;